import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { RootState } from '@/redux/store';

const selectSidebarPermissions = (state: RootState) => state.auth.sidebarPermission;
const selectFullPermissions = (state: RootState) => state.auth.permission;
const selectProjectPermissions = (state: RootState) => state.auth.projectPermissions;
const selectCompanyPermissions = (state: RootState) => state.auth.companyPermissions;
const selectUserPermissions = (state: RootState) => state.auth.userPermissions;

const selectPermissions = createSelector(
  [
    selectSidebarPermissions,
    selectFullPermissions,
    selectProjectPermissions,
    selectCompanyPermissions,
    selectUserPermissions,
    (_state: RootState, mode: 'sidebar' | 'full') => mode,
  ],
  (sidebarPermissions, fullPermissions, projectPermissions, companyPermissions, userPermissions, mode) => {
    if (mode === 'sidebar') {
      return sidebarPermissions || {};
    }

    return {
      ...(userPermissions || {}),
      ...(companyPermissions || {}),
      ...(projectPermissions || {}),
      ...(fullPermissions || {}),
    };
  },
);

export const usePermissions = (mode: 'sidebar' | 'full' = 'full') => {
  const permissions = useSelector((state: RootState) => selectPermissions(state, mode));

  const hasPermission = (key?: string): boolean => {
    if (!key) return true;
    return permissions[key] === 1;
  };
  return hasPermission;
};

export const useProjectPermissions = () => {
  const projectPermissions = useSelector((state: RootState) => selectProjectPermissions(state)) || {};
  const companyPermissions = useSelector((state: RootState) => selectCompanyPermissions(state)) || {};
  const userPermissions = useSelector((state: RootState) => selectUserPermissions(state)) || {};
  const hasProjectSpecificPermissions = Object.keys(projectPermissions).length > 0;
  const globalPermissions = Object.keys(companyPermissions).length > 0 ? companyPermissions : userPermissions;

  const hasPermission = (key?: string): boolean => {
    if (!key) return true;
    if (!hasProjectSpecificPermissions) return true;

    if (Object.prototype.hasOwnProperty.call(projectPermissions, key)) {
      return projectPermissions[key] === 1;
    }
    return globalPermissions[key] === 1;
  };
  return hasPermission;
};
